// pages/payroll/YearlyBattaSalaryScale.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, Printer, FileSpreadsheet, Users, MapPin, CalendarDays } from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const YearlyBattaSalaryScale = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || ""; // adjust if your key differs
  const branchType = localStorage.getItem("branchtType") || "HO"; // adjust if your key differs

  const screenBadge = useMemo(() => "INFRA SCREEN : C-60", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // Meta
  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [years, setYears] = useState([]);
  const [defaultYear, setDefaultYear] = useState("");
  const [lockBranch, setLockBranch] = useState(false);
  const [canViewAll, setCanViewAll] = useState(false);

  // Filters
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  const [payment, setPayment] = useState("Select"); // 1 Cash, 2 Bank
  const [siteId, setSiteId] = useState("Select"); // ddl_Site value (branch id)
  const [deptId, setDeptId] = useState("Select");
  const [year, setYear] = useState("Select");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [tdsLimit, setTdsLimit] = useState("Select"); // 1/2/3
  const [radio, setRadio] = useState("Salary"); // Salary | Bata

  // Autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  // Grid + pagination
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [footer, setFooter] = useState({ totalSum: 0 });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const columns = useMemo(() => {
    if (!rows?.length) return [];
    // ASP Grid AutoGenerateColumns=true; we keep column order based on keys returned
    return Object.keys(rows[0] || {});
  }, [rows]);

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || !String(branchType).trim()) {
      toast.warn("Branch info missing (branchid/branchtype).");
      return;
    }

    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/yearly-batta-salary-scale/meta`, {
        params: { finYearId: Number(finYearId), branchId: Number(branchId), branchType: String(branchType) },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setDepartments(res.data.departments || []);
        setYears(res.data.years || []);
        setDefaultYear(String(res.data.defaultYear ?? ""));
        setLockBranch(!!res.data.lockBranch);
        setCanViewAll(!!res.data.canViewAll);

        const defBranch = String(res.data.defaultBranchId ?? "");
        setSiteId(defBranch || "Select");

        setRadio(res.data.defaults?.radio || "Salary");
        setFromDate(res.data.defaults?.fromDate || "");
        setToDate(res.data.defaults?.toDate || "");

        const dy = String(res.data.defaultYear ?? "");
        setYear(dy ? dy : "Select");
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchData = async (p = 1) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/yearly-batta-salary-scale/data`, {
        params: {
          finYearId: Number(finYearId),
          radio,
          empCode: empCode.trim(),
          empName: empName.trim(),
          siteId,
          payment,
          deptId,
          year: year === "Select" ? defaultYear : year,
          fromDate,
          toDate,
          tdsLimit,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setFooter(res.data.footer || { totalSum: 0 });
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta().then(() => setTimeout(() => fetchData(1), 50));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // AutoComplete – debounce
  useEffect(() => {
    const t = setTimeout(async () => {
      const q = empCode.trim();
      if (!q) return setCodeSug([]);
      try {
        const res = await axios.get(`${BASE_URL}/api/yearly-batta-salary-scale/empcode-suggest`, {
          params: {
            finYearId: Number(finYearId),
            branchId: siteId === "Select" ? "" : siteId,
            branchType,
            prefix: q,
          },
        });
        if (res?.data?.success) setCodeSug(res.data.items || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [empCode, finYearId, siteId, branchType]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = empName.trim();
      if (!q) return setNameSug([]);
      try {
        const res = await axios.get(`${BASE_URL}/api/yearly-batta-salary-scale/empname-suggest`, {
          params: {
            finYearId: Number(finYearId),
            branchId: siteId === "Select" ? "" : siteId,
            branchType,
            prefix: q,
          },
        });
        if (res?.data?.success) setNameSug(res.data.items || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [empName, finYearId, siteId, branchType]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();

    // ASP Validate() only checked Site not Select; but your page also has ViewAll.
    if (siteId === "Select" && !canViewAll) {
      toast.warn("Please Select Branch..");
      return;
    }
    setPage(1);
    await fetchData(1);
  };

  const handleViewAll = async () => {
    // ASP sets ddl_Site.SelectedIndex=0 then filldatagrid1()
    setSiteId("Select");
    setPage(1);
    await fetchData(1);
  };

  const openExcel = (type) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (rows.length === 0 && type === "main") {
      toast.warn("No records for Excel");
      return;
    }

    const urlParams = new URLSearchParams({
      finYearId: String(finYearId),
      radio,
      empCode: empCode.trim(),
      empName: empName.trim(),
      siteId,
      payment,
      deptId,
      year: year === "Select" ? String(defaultYear) : String(year),
      fromDate,
      toDate,
      tdsLimit,
    });

    let url = "";
    if (type === "main") url = `${BASE_URL}/api/yearly-batta-salary-scale/excel?${urlParams.toString()}`;
    if (type === "dep") url = `${BASE_URL}/api/yearly-batta-salary-scale/dep-excel?${urlParams.toString()}`;
    if (type === "branch") url = `${BASE_URL}/api/yearly-batta-salary-scale/branch-excel?${urlParams.toString()}`;

    window.location.href = url;
  };

  const handlePrint = () => {
    const urlParams = new URLSearchParams({
      finYearId: String(finYearId),
      radio,
      empCode: empCode.trim(),
      empName: empName.trim(),
      siteId,
      payment,
      deptId,
      year: year === "Select" ? String(defaultYear) : String(year),
      fromDate,
      toDate,
      tdsLimit,
    });
    window.open(`/PayRoll/YearlyBattaSalaryScale_Print?${urlParams.toString()}`, "_blank", "width=1200,height=900,scrollbars=yes");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar title={<span className="font-dmserif text-3xl">YEARLY BATTA & SALARY SCALE</span>} setSkin={setSkin} currentSkin={skin} />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Header Row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>Yearly Batta & Salary Scale</div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Search • View All • Excel • Print</div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchData(page)} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${loading || metaLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={ghostBtn} disabled={loading || metaLoading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              {/* Emp Code */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <input
                  value={empCode}
                  onChange={(e) => {
                    setEmpCode(e.target.value);
                    setShowCodeSug(true);
                  }}
                  onFocus={() => setShowCodeSug(true)}
                  onBlur={() => setTimeout(() => setShowCodeSug(false), 150)}
                  placeholder="Type Employee Code..."
                  className={`mt-2 ${inputBase}`}
                />
                {showCodeSug && codeSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {codeSug.slice(0, 10).map((it) => (
                      <button
                        key={it}
                        type="button"
                        onClick={() => {
                          setEmpCode(it);
                          setShowCodeSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"}`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Emp Name */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <input
                  value={empName}
                  onChange={(e) => {
                    setEmpName(e.target.value);
                    setShowNameSug(true);
                  }}
                  onFocus={() => setShowNameSug(true)}
                  onBlur={() => setTimeout(() => setShowNameSug(false), 150)}
                  placeholder="Type Employee Name..."
                  className={`mt-2 ${inputBase}`}
                />
                {showNameSug && nameSug.length > 0 && (
                  <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {nameSug.slice(0, 10).map((it) => (
                      <button
                        key={it}
                        type="button"
                        onClick={() => {
                          setEmpName(it);
                          setShowNameSug(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"}`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment */}
              <div>
                <label className={labelBase}>Payment Mode</label>
                <select value={payment} onChange={(e) => setPayment(e.target.value)} className={`mt-2 ${inputBase}`}>
                  <option value="Select">Select</option>
                  <option value="1">Cash</option>
                  <option value="2">Bank</option>
                </select>
              </div>

              {/* Site */}
              <div>
                <label className={labelBase}>Site</label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className={inputBase} disabled={lockBranch}>
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dept */}
              <div>
                <label className={labelBase}>Department</label>
                <div className="mt-2 flex items-center gap-2">
                  <Users className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select value={deptId} onChange={(e) => setDeptId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {departments.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>Year</label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={`mt-2 ${inputBase}`}>
                  <option value="Select">Select</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {/* From/To date - IMPORTANT: input type date */}
              <div>
                <label className={labelBase}>From Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputBase} />
                </div>
              </div>

              <div>
                <label className={labelBase}>To Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputBase} />
                </div>
              </div>

              {/* TDS Limit */}
              <div>
                <label className={labelBase}>TDS Limit</label>
                <select value={tdsLimit} onChange={(e) => setTdsLimit(e.target.value)} className={`mt-2 ${inputBase}`}>
                  <option value="Select">Select</option>
                  <option value="1">1–3 lakh</option>
                  <option value="2">3–5 lakh</option>
                  <option value="3">5-above lakh</option>
                </select>
              </div>

              {/* Radio */}
              <div className="flex items-end gap-4">
                <label className={`${labelBase} flex items-center gap-2`}>
                  <input type="radio" checked={radio === "Salary"} onChange={() => setRadio("Salary")} />
                  Salary
                </label>
                <label className={`${labelBase} flex items-center gap-2`}>
                  <input type="radio" checked={radio === "Bata"} onChange={() => setRadio("Bata")} />
                  Bata
                </label>
              </div>

              {/* Buttons */}
              <div className="xl:col-span-4 flex flex-wrap items-center justify-center gap-3 mt-2">
                <button type="submit" className={primaryBtn} disabled={loading || metaLoading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>

                {canViewAll && (
                  <button type="button" onClick={handleViewAll} className={ghostBtn} disabled={loading || metaLoading}>
                    View All
                  </button>
                )}

                <button type="button" onClick={() => openExcel("dep")} className={ghostBtn} disabled={loading || metaLoading}>
                  <FileSpreadsheet className="w-4 h-4" />
                  Dept Excel
                </button>

                <button type="button" onClick={() => openExcel("branch")} className={ghostBtn} disabled={loading || metaLoading}>
                  <FileSpreadsheet className="w-4 h-4" />
                  Branch Excel
                </button>

                <button
                  type="button"
                  onClick={() => openExcel("main")}
                  className={ghostBtn}
                  disabled={loading || metaLoading || rows.length === 0}
                  title={rows.length === 0 ? "No rows" : "Download"}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    {columns.map((c) => (
                      <th key={c} className="px-4 py-4 text-center font-bold">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 1 + Math.max(columns.length, 6) }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={1 + Math.max(columns.length, 1)} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        {columns.map((c) => (
                          <td key={c} className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {String(r?.[c] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>

                {/* Footer like ASP: Total sum for column "Total" */}
                {!loading && rows.length > 0 && columns.includes("Total") && (
                  <tfoot>
                    <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                      <td className="px-4 py-4 font-bold text-right">Total</td>
                      {columns.map((c) => (
                        <td key={c} className="px-4 py-4 font-bold text-center">
                          {c === "Total" ? footer.totalSum : ""}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1 ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={() => fetchData(page)} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${loading || metaLoading ? "animate-spin" : ""}`} />
                Refresh Grid
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default YearlyBattaSalaryScale;